import { create } from "zustand";
import { apiClient } from "@/api/client";
import { AxiosError } from "axios";

interface Language {
  id: string;
  name: string;
  desc?: string;
  media_agency_id: string;
}

interface LanguageResponse {
  data: Language[];
}

interface FormData {
  title: string;
  description: string;
  media_agency_id: string;
  isLoading: boolean;
}

interface LanguageState {
  languages: Language[] | null;
  formData: FormData;
  setFormData: (data: Partial<FormData>) => void;
  resetFormData: () => void;
  getLanguages: () => Promise<void>;
  addLanguage: () => Promise<void>;
  editLanguage: (id: string) => Promise<void>;
  deleteLanguage: (id: string) => Promise<void>;
  setFormDataForEdit: (language: Language) => void;
  setLanguages: (languages: Language[]) => void;
}

const initialFormData = {
  title: "",
  description: "",
  media_agency_id: "",
  isLoading: false,
};

const useLanguageStore = create<LanguageState>((set, get) => ({
  languages: null,

  formData: { ...initialFormData },

  resetFormData: () => set({ formData: { ...initialFormData } }),

  setFormData: (data) =>
    set((state) => ({
      formData: {
        ...state.formData,
        ...data,
      },
    })),

  setFormDataForEdit: (language) => {
    set({
      formData: {
        title: language.name,
        description: language.desc || "",
        media_agency_id: language.media_agency_id || "",
        isLoading: false,
      },
    });
  },

  setLanguages: (languages: Language[]) => set({ languages }),

  getLanguages: async () => {
    try {
      const response = await apiClient.get<LanguageResponse>("/languages");
      set({ languages: response.data.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  addLanguage: async () => {
    const { formData, languages, setFormData } = get();
    setFormData({ isLoading: true });

    try {
      const response = await apiClient.post("/languages", {
        name: formData.title,
        desc: formData.description,
        media_agency_id: formData.media_agency_id,
      });

      const newLang = response.data;
      get().setLanguages([...(languages ?? []), newLang]);

      setFormData({
        title: "",
        description: "",
        media_agency_id: "",
        isLoading: false,
      });
    } catch (error) {
      setFormData({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  editLanguage: async (id) => {
    const { formData, languages, setFormData } = get();
    setFormData({ isLoading: true });

    try {
      const response = await apiClient.put(`/languages/${id}`, {
        name: formData.title,
        desc: formData.description,
        media_agency_id: formData.media_agency_id,
      });

      const updatedLang = response.data;
      const updatedLanguages = (languages ?? []).map((lang) =>
        lang.id === id ? updatedLang : lang
      );
      get().setLanguages(updatedLanguages);

      setFormData({
        title: "",
        description: "",
        media_agency_id: "",
        isLoading: false,
      });
    } catch (error) {
      setFormData({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  deleteLanguage: async (id) => {
    try {
      await apiClient.delete(`/languages/${id}`);

      const updatedLanguages = (get().languages ?? []).filter(
        (lang) => lang.id !== id
      );
      get().setLanguages(updatedLanguages);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useLanguageStore;
